import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, ErrorComponent } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'
import { Grid } from '@mui/material'

import { NoPermissionError } from '@/api/NoPermissionError'
import {
    organizationDynamicFieldsSchema,
    OrganizationDynamicFieldsType,
} from '@/api/organizations'
import {
    organizationDynamicFieldsQueryOptions,
    useUpdateOrganizationDynamicFieldsMutation,
} from '@/api/organizationsQueryOptions'
import OrganizationDynamicTextFields from '@/components/form/org/OrganizationDynamicTextFields'
import FormDebug from '@/components/FormDebug'
import { NoPermissionErrorComponent } from '@/components/NoPermissionErrorComponent'
import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'
import { useForm } from 'react-hook-form'

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/dynamicFields'
)({
    component: OrganizationComponent,
    errorComponent: ({ error }) => {
        if (error instanceof NoPermissionError) {
            return (
                <NoPermissionErrorComponent error={error}></NoPermissionErrorComponent>
            )
        }
        return <ErrorComponent error={error}></ErrorComponent>
    },
})

function OrganizationComponent() {
    const params = Route.useParams()

    const organizationDynamicFieldsQuery = useSuspenseQuery(
        organizationDynamicFieldsQueryOptions(params.organizationUuid),
    )
    const organizationDynamicFields = organizationDynamicFieldsQuery.data

    const updateOrganizationDynamicFieldsMutation =
        useUpdateOrganizationDynamicFieldsMutation(params.organizationUuid)

    const {
        control,
        register,
        handleSubmit,
        formState: { isDirty, isValid, errors },
    } = useForm<OrganizationDynamicFieldsType>({
        resolver: yupResolver<OrganizationDynamicFieldsType, any, any>(
            organizationDynamicFieldsSchema,
        ),
        mode: 'onBlur',
        shouldFocusError: true,
    })

    const onSubmit = (data: OrganizationDynamicFieldsType) => {
        if (isValid) {
            console.log(JSON.stringify({ ...organizationDynamicFields, ...data }))
            updateOrganizationDynamicFieldsMutation.mutate({
                ...organizationDynamicFields,
                ...data,
            })
            enqueueSnackbar(t('action.changed', { entity: t('entity.organization') }))
        } else {
            console.log(errors)
        }
    }

    const onError = () => {
        console.log('error')
    }

    const writeable = organizationDynamicFields.permissions
        ? organizationDynamicFields.permissions.some((perm) => perm === 'write')
        : false

    if (
        organizationDynamicFieldsQuery.isLoading ||
        organizationDynamicFieldsQuery.isFetching
    ) {
        return <LoadProgress />
    }

    return (<>
        <form onBlur={handleSubmit(onSubmit, onError)}>
            <Grid container spacing={2}>
                <input
                    type="hidden"
                    {...register('uuid')}
                    defaultValue={organizationDynamicFields.uuid}
                />
                <Grid
                    size={{
                        xs: 12,
                        md: 4
                    }}>
                    <OrganizationDynamicTextFields
                        register={register}
                        scope="dynamicField"
                        organizationDynamicFields={organizationDynamicFields}
                        writeable={writeable}
                    />
                </Grid>

                <Grid
                    size={{
                        xs: 12,
                        md: 4
                    }}>
                    <OrganizationDynamicTextFields
                        register={register}
                        scope="checkField"
                        organizationDynamicFields={organizationDynamicFields}
                        writeable={writeable}
                    />
                </Grid>

                <Grid
                    size={{
                        xs: 12,
                        md: 4
                    }}>
                    <OrganizationDynamicTextFields
                        register={register}
                        scope="dateField"
                        organizationDynamicFields={organizationDynamicFields}
                        writeable={writeable}
                    />
                </Grid>
            </Grid>
        </form>
        <FormDebug
            errors={errors}
            dirty={isDirty}
            valid={isValid}
            value={organizationDynamicFields}
        />
        {import.meta.env.VITE_DEBUG === 'true' &&
            <DevTool control={control} />
        }
    </>);
}
